import React, { useState } from 'react';
import clsx from 'clsx';
import { useField } from 'formik';
import { Box, Grid, IconButton, InputAdornment, Tooltip, InputBaseProps } from '@mui/material';
import { VisibilityOffOutlined, VisibilityOutlined, InfoOutlined } from '@mui/icons-material';
import { Alert } from '../../Alert';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { ValidationIndicator, ValidationIndicatorProps } from '../../indicator/validation-indicator.component';
import { BaseTextField } from '../BaseTextField';
import { useStyles } from './Password.styles';

// eslint-disable-next-line no-useless-escape
const specialCharactersRegex = /[~`!@#\$%\^&\*\(\)_\-\+={\[\}\]\|\\:;“’<,>\.\?\/]/;
const specialCharactersString = '~ ` ! @ # $ % ^ & * ( ) _ - + = { [ } ] | \\ : ; “ ’ < , > . ? /';
const minLength = 8;
const maxLength = 125;

type ValidationState = {
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  specialCharacter: boolean;
  minLength: boolean;
  underMaxLength: boolean;
};

export type PasswordFieldProps = InputBaseProps & {
  label?: string;
  name: string;
  placeholder?: string;
  id?: string;
  className?: string;
  minLength?: number;
  stackValidatorsOnMobile?: boolean;
};

type AdornmentProps = {
  handleClick: () => void;
  className?: string;
  showPassword: boolean;
};

export const PasswordAdornment: React.VFC<AdornmentProps> = React.memo(({ handleClick, className, showPassword }) => (
  <InputAdornment position="end">
    <IconButton
      aria-label="toggle password visibility"
      onClick={handleClick}
      edge="end"
    >
      {showPassword
        ? <VisibilityOutlined className={className} />
        : <VisibilityOffOutlined className={className} />}
    </IconButton>
  </InputAdornment>
));

type SpecialCharactorValidatorProps = {
  stackValidators: boolean;
} & ValidationIndicatorProps;

const SpecialCharacterValidator: React.VFC<SpecialCharactorValidatorProps> = ({ stackValidators, ...validatorProps }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const [ open, setOpen ] = useState(false);

  const icon = stackValidators
    ? (
      <Box onClick={() => setOpen(p => !p)} display="flex">
        <InfoOutlined className={classes.infoIcon} />
      </Box>
    )
    : (
      <Tooltip title={specialCharactersString} placement="right" enterTouchDelay={0}>
        <InfoOutlined className={classes.infoIcon} />
      </Tooltip>
    );

  return (
    <div>
      <ValidationIndicator {...validatorProps}>
        <Box display="flex" alignItems="center">
          {l10n.validations.specialCharacter}
          {icon}
        </Box>
      </ValidationIndicator>
      {stackValidators && open && (
        <div className={classes.tooltip}>
          {specialCharactersString}
        </div>
      )}
    </div>
  );
};

export const PasswordField: React.VFC<PasswordFieldProps> = (props) => {
  const isMobile = useIsMobile('sm');
  const [ showPassword, setSeePassword ] = React.useState<boolean>(false);
  const [ validationState, setValidationState ] = React.useState<ValidationState>({
    uppercase: false,
    lowercase: false,
    number: false,
    specialCharacter: false,
    minLength: false,
    underMaxLength: true,
  });
  const { classes } = useStyles();
  const l10n = useL10n();

  const validate = (value: string) => {
    const hasUpper = Boolean(value && !(value.toLowerCase() === value));
    const hasLower = Boolean(value && !(value.toUpperCase() === value));
    const hasNumber = /\d/.test(value);
    const hasSpecial = specialCharactersRegex.test(value);
    const hasMinLength = value?.length >= minLength;
    const hasUnderMaxLength = value?.length <= maxLength;

    setValidationState({
      uppercase: hasUpper,
      lowercase: hasLower,
      number: hasNumber,
      specialCharacter: hasSpecial,
      minLength: hasMinLength,
      underMaxLength: hasUnderMaxLength,
    });

    return (hasUpper && hasLower && hasNumber && hasSpecial && hasMinLength)
      ? ''
      : 'error';
  };

  const [ field, meta ] = useField({
    name: props.name,
    validate,
  });

  const handleClick = React.useCallback(() => {
    setSeePassword(!showPassword);
  }, [ showPassword ]);

  const isValid =
    validationState.lowercase &&
    validationState.uppercase &&
    validationState.number &&
    validationState.specialCharacter &&
    validationState.minLength;

  const stackValidators = Boolean(props.stackValidatorsOnMobile && isMobile);
  const validatorClassName = stackValidators ? classes.font16 : classes.font14;

  const bottom = isValid
    ? <Alert severity="success" className={classes.alert}>{l10n.validations.isSecure}</Alert>
    : (
      <Grid
        container
        className={classes.spacer}
        size={{
          xs: 12,
          sm: 10
        }}
      >
        <Grid size={stackValidators ? 12 : 6}>
          <Box display="flex" flexDirection="column" gap="0.25rem" mb={stackValidators ? 0.5 : 0}>
            <ValidationIndicator touched={meta.touched} isValid={validationState.lowercase} className={validatorClassName}>
              {l10n.validations.lowercaseLetter}
            </ValidationIndicator>
            <ValidationIndicator touched={meta.touched} isValid={validationState.uppercase} className={validatorClassName}>
              {l10n.validations.uppercaseLetter}
            </ValidationIndicator>
            <ValidationIndicator touched={meta.touched} isValid={validationState.number} className={validatorClassName}>
              {l10n.validations.aNumber}
            </ValidationIndicator>
          </Box>
        </Grid>
        <Grid size={stackValidators ? 12 : 6}>
          <Box display="flex" flexDirection="column" gap="0.25rem">
            <SpecialCharacterValidator touched={meta.touched} isValid={validationState.specialCharacter} stackValidators={stackValidators} className={validatorClassName} />
            <ValidationIndicator touched={meta.touched} isValid={validationState.minLength} className={validatorClassName}>
              {l10n.validations.minLength(props?.minLength ?? minLength)}
            </ValidationIndicator>
          </Box>
        </Grid>
      </Grid>
    );

  const showHelperText = !validationState.underMaxLength;

  return (
    <BaseTextField
      fullWidth
      id={props.id}
      className={clsx(classes.formControl, props.className)}
      label={props.label}
      type={showPassword ? 'text' : 'password'}
      error={meta.touched && !!meta.error}
      helperText={showHelperText ? meta.error : bottom}
      placeholder={props.placeholder}
      {...field}
      slotProps={{
        input: {
          className: 'HELLO',
          endAdornment: <PasswordAdornment handleClick={handleClick} className={classes.seePasswordIcon} showPassword={showPassword} />
        }
      }}
      color="secondary"
      disabled={props.disabled}
    />
  );
};
